<!--
  MembraneAddDataCollection.vue - Data Source Configuration Wizard

  This component is a multi-step wizard for adding and configuring new data sources from Membrane.
  It handles the complex process of:
  1. Selecting an account (connection) and data collection
  2. Dynamically rendering form fields based on JSON schema
  3. Handling reference collections with search and pagination
  4. Managing complex dropdown interactions for searchable vs non-searchable fields

  Key Membrane Concepts:
  - Data Collection: A type of resource from an integration (e.g., "repositories", "issues", "users")
  - Parameters Schema: JSON Schema defining required configuration fields for a data collection
  - Reference Collection: A field that references another data collection (e.g., repository field references repositories)
  - Searchable Fields: Reference collections that support server-side search (vs. client-side filtering)

  Complex UI Systems:

  1. Dynamic Field Rendering:
     - Reads JSON Schema and generates appropriate input components
     - Handles different field types: strings, enums, reference collections
     - Supports nested parameter dependencies (field A affects options in field B)

  2. Reference Collection Handling:
     - Detects searchable vs non-searchable collections by checking API capabilities
     - For searchable: Uses custom dropdown with server-side search (debounced, race-condition protected)
     - For non-searchable: Uses standard BaseInputSelect with all data loaded upfront

  3. Pagination Workarounds:
     - Handles Membrane cursor pagination bugs (same cursor returned)
     - Implements "first page only" fallback when cursor bug is detected
     - Loads all pages for non-searchable collections, stops at first page for searchable ones

  4. Search Implementation:
     - 300ms debounced search with minimum 3 characters
     - Race condition protection using request IDs
     - Real-time results without losing focus (custom dropdown vs BaseInputSelect)
     - Preserves Membrane's relevance ordering (no client-side sorting)

  5. Parameter Dependencies:
     - Supports field references like "$repositoryId" in parameter definitions
     - Automatically reloads dependent fields when referenced parameters change
     - Clears dependent field values when parent changes

  Data Flow:
  1. User selects account → loads available data collections for that connection
  2. User selects data collection → fetches parameter schema and loads reference data
  3. For each reference field → determines if searchable and loads appropriate data
  4. User fills parameters → validates and creates data source configuration in Routine

  State Management:
  - Reactive computed properties for field options based on search state
  - Complex synchronization between search queries, results, and UI state
  - Cleanup on navigation to prevent memory leaks and stale data
-->
<template>
  <div
    v-if="resolvedApp"
    :class="
      modal
        ? 'add-data-collection-modal-body'
        : 'settings-page add-data-collection'
    "
  >
    <h2 v-if="!modal">
      <img :src="resolvedApp.logoUri" class="icon" />Add Data Source
    </h2>

    <div class="form-section">
      <div class="field">
        <label>Account <span class="required">*</span></label>
        <BaseInputSelect
          :value="selectedConnectionDisplay"
          :options="connectionOptions"
          placeholder="Select an account"
          :disabled="connectionOptions.length === 0"
          select-only
          @selected="onConnectionSelected"
        />
      </div>

      <div class="field">
        <label>Select data collection <span class="required">*</span></label>
        <div class="input-with-spinner">
          <BaseInputSelect
            :value="selectedDataCollection?.key ?? ''"
            :options="dataCollectionOptions"
            placeholder="Select data collection"
            :disabled="!selectedConnectionId || dataCollectionsLoading"
            select-only
            @selected="onDataCollectionSelected"
          />
          <Spinner v-if="dataCollectionsLoading" />
        </div>
      </div>

      <!-- Dynamic parameter fields based on schema -->
      <div v-if="specLoading" class="dynamic-fields-loading">
        <div class="field">
          <label>Loading data source parameters...</label>
          <div class="loading-container">
            <Spinner />
          </div>
        </div>
      </div>

      <!-- oneOf schema error -->
      <div v-else-if="oneOfError" class="field">
        <div class="error-message">
          {{ oneOfError }}
        </div>
      </div>

      <div v-else-if="dynamicFields.length > 0" class="dynamic-fields">
        <div v-for="field in dynamicFields" :key="field.key" class="field">
          <label>
            {{ field.label }}
            <span v-if="field.required" class="required">*</span>
          </label>

          <small v-if="field.description" class="field-description">
            {{ field.description }}
          </small>

          <!-- Integration-specific guidance (e.g. Slack private channels) -->
          <BaseAlert
            v-if="fieldNoteSegments[field.key]"
            class="field-note"
            state="info"
            size="small"
          >
            <InfoIcon class="field-note-icon" />
            <span>
              <template
                v-for="(segment, index) in fieldNoteSegments[field.key]"
                :key="index"
                ><code v-if="segment.code">{{ segment.value }}</code
                ><template v-else>{{ segment.value }}</template></template
              >
            </span>
          </BaseAlert>

          <!-- For reference collection fields -->
          <div v-if="field.referenceCollection" class="input-with-spinner">
            <!-- Use custom search input for searchable fields -->
            <div
              v-if="referenceCollectionSearchable[field.key]"
              class="reference-collection-input"
            >
              <BaseInput
                :ref="`searchInput-${field.key}`"
                display="form"
                :value="
                  referenceCollectionSelectedLabels[field.key] ||
                  referenceCollectionCurrentQuery[field.key] ||
                  ''
                "
                :placeholder="getFieldPlaceholder(field)"
                :is-disabled="isFieldDisabled(field.key)"
                :prevent-default-on-keys="['up', 'down', 'enter']"
                @update:value="
                  (value) => handleReferenceCollectionInput(field.key, value)
                "
                @focus="() => handleReferenceCollectionFocus(field.key)"
                @blur="() => handleReferenceCollectionBlur(field.key)"
                @up="() => selectPreviousOption(field.key)"
                @down="() => selectNextOption(field.key)"
                @enter="() => validateReferenceCollectionSelection(field.key)"
              />
              <div
                v-if="
                  referenceCollectionDropdownOpen[field.key] &&
                  (referenceCollectionOptions[field.key]?.length > 0 ||
                    referenceCollectionLoading[field.key])
                "
                class="reference-collection-dropdown"
                @mousedown.stop.prevent
              >
                <div
                  v-for="(option, i) in referenceCollectionOptions[field.key]"
                  :key="option.value"
                  class="dropdown-item"
                  :class="{
                    selected: i === referenceCollectionSelectedIndex[field.key],
                  }"
                  @click="selectReferenceCollectionOption(field.key, option)"
                  @mouseover="referenceCollectionSelectedIndex[field.key] = i"
                >
                  {{ option.label }}
                </div>
              </div>
            </div>

            <!-- Use BaseInputSelect for non-searchable fields -->
            <BaseInputSelect
              v-else
              :value="referenceCollectionSelectedLabels[field.key] || ''"
              :options="referenceCollectionOptions[field.key] || []"
              :placeholder="getFieldPlaceholder(field)"
              :disabled="isFieldDisabled(field.key)"
              @selected="
                (option) => {
                  referenceCollectionSelectedLabels[field.key] = option.label;
                  onParameterChange(field.key, option.value);
                }
              "
            />

            <div
              v-if="referenceCollectionLoading[field.key]"
              class="spinner-overlay"
            >
              <Spinner />
            </div>
          </div>

          <!-- For enum fields, use select -->
          <BaseInputSelect
            v-else-if="field.enum"
            :value="getParameterStringValue(field.key)"
            :options="
              field.enum
                .map((val) => ({
                  value: val,
                  label: val,
                  aliases: [],
                }))
                .sort((a: SelectOption, b: SelectOption) =>
                  a.label.localeCompare(b.label)
                )
            "
            :placeholder="`Select ${field.label}`"
            :disabled="specLoading"
            @selected="(option) => onParameterChange(field.key, option.value)"
          />

          <!-- For other fields, use regular input -->
          <FormInput
            v-else
            size="caption"
            :value="getParameterStringValue(field.key)"
            :placeholder="field.placeholder"
            :state="specLoading ? 'disabled' : 'default'"
            @update:value="onParameterChange(field.key, $event)"
          />
        </div>
      </div>

      <!-- Workspace selection - always visible -->
      <div class="field">
        <label>Select Routine workspace <span class="required">*</span></label>
        <BaseInputSelect
          :value="selectedWorkspace?.id ?? ''"
          :options="workspaceOptions"
          placeholder="Select workspace"
          select-only
          @selected="onWorkspaceSelected"
        />
      </div>

      <div class="field">
        <label
          >Pick a name for your data source
          <span class="required">*</span></label
        >
        <FormInput
          size="caption"
          :value="databaseDisplayName"
          type="text"
          placeholder="Enter data source name"
          @update:value="(value) => (databaseDisplayName = value)"
          @keydown="
            (event: KeyboardEvent) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                onConfirm();
              }
            }
          "
        />
      </div>

      <!-- Final configuration – only shown when not in modal (modal provides footer buttons) -->
      <div v-if="selectedDataCollection && !modal" class="final-fields">
        <div class="form-actions">
          <BaseButton
            kind="important"
            type="primary"
            text="Confirm"
            :state="isFormValid ? 'default' : 'disabled'"
            @click="onConfirm"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import {
  Connection,
  DataCollectionSpec,
  DataSchema,
  Integration,
} from "@membranehq/sdk";
import { computed, defineComponent, ref, Ref, toRaw, watch } from "vue";
import type { PropType } from "vue";

import {
  IntegrationAppConnectionId,
  IntegrationAppEnabledDataCollectionId,
  WorkspaceId,
} from "@ctrl/controller-rpc";

import BaseAlert from "@/components/base/BaseAlert.vue";
import BaseButton from "@/components/base/BaseButton.vue";
import BaseInput from "@/components/base/BaseInput.vue";
import BaseInputSelect, {
  SelectOption,
} from "@/components/base/BaseInputSelect.vue";
import FormInput from "@/components/graph/definition/form/FormInput.vue";
import InfoIcon from "@/components/icons/InfoIcon.vue";
import * as inject from "@/components/inject";
import { getAllowedDataCollections } from "@/components/settings/integrations/integrationAllowlist";
import {
  getIntegrationFieldNote,
  NoteSegment,
  noteSegments,
} from "@/components/settings/integrations/integrationFieldNotes";
import {
  DynamicField,
  useMembraneDynamicSchemaForm,
} from "@/composables/useMembraneDynamicSchemaForm";
import {
  isVarReference,
  useMembraneReferenceCollections,
} from "@/composables/useMembraneReferenceCollections";
import { useReleaser } from "@/ref";
import { RouteNames } from "@/routes/types";
import { normalizeVarReference } from "@/utils/membrane";
import { navigateTo } from "@/utils/navigate";

export default defineComponent({
  components: {
    BaseAlert,
    BaseButton,
    FormInput,
    InfoIcon,
    BaseInput,
    BaseInputSelect,
  },
  props: {
    orgId: { type: String, required: true },
    /** Route usage: the integration's key, from the `:appKey` route param. */
    appKey: { type: String, default: "" },
    /** Modal usage: the parent workflow passes its already-resolved app. */
    app: {
      type: Object as PropType<Integration | null>,
      required: false,
      default: null,
    },
    modal: { type: Boolean, default: false },
    onDone: {
      type: Function as PropType<() => void>,
      required: false,
      default: null,
    },
    onValidityChange: {
      type: Function as PropType<(valid: boolean) => void>,
      required: false,
      default: null,
    },
    onSubmitReady: {
      type: Function as PropType<(submit: () => Promise<void>) => void>,
      required: false,
      default: null,
    },
  },
  async setup(props) {
    const controller = inject.controller();
    const router = inject.router();
    const integrations = inject.integrations();
    const releaser = useReleaser();
    const organizationId = computed(() => props.orgId);
    const view = integrations.forOrganization(organizationId);

    // Modal usage gets the app from the parent workflow; route usage resolves
    // it from the `:appKey` param via the store.
    const resolvedApp = computed<Integration | null>(
      () =>
        props.app ??
        (props.appKey ? (view.getServiceByKey(props.appKey) ?? null) : null)
    );

    // Route usage with a key that stays unresolved once the organization's
    // services have loaded (bad deep link, integration removed): redirect to
    // the integrations list. Not applicable to the embedded/modal usage.
    watch(
      [resolvedApp, view.loading],
      ([resolved, loading]) => {
        if (props.modal || loading || resolved) return;
        void router.replace({
          name: RouteNames.SettingsIntegrations,
          params: { orgId: props.orgId },
        });
      },
      { immediate: true }
    );

    const selectedConnectionId = ref<IntegrationAppConnectionId>("");
    const selectedConnectionDisplay = ref<string>("");
    type SelectedDataCollection = { key: string; name: string } | null;
    const selectedDataCollection = ref<SelectedDataCollection>(null);
    // The organization's connections, from the app-wide integrations store.
    const allConnections = view.connections;

    // Final configuration fields
    type SelectedWorkspace = { id: WorkspaceId; name: string } | null;
    const selectedWorkspace = ref<SelectedWorkspace>(null);
    const databaseDisplayName = ref<string>("");

    // The store's long-lived per-organization Membrane client.
    const membrane = computed(() => integrations.client(organizationId.value));

    // Set up computed properties and watchers BEFORE any await.
    // Evaluated eagerly by the immediate `connectionOptions` watcher below,
    // so it must tolerate a null current app (direct route restore).
    const connections: Ref<Array<Connection>> = computed(() =>
      allConnections.value.filter(
        (connection) => connection.integrationId === resolvedApp.value?.id
      )
    );

    const connectionOptions: Ref<Array<SelectOption>> = computed(() =>
      connections.value
        .map((connection) => ({
          value: connection.name,
          label: connection.name,
          aliases: [],
        }))
        .sort((a: SelectOption, b: SelectOption) =>
          a.label.localeCompare(b.label)
        )
    );

    type DataCollectionListItem = { key: string; name?: string };
    const availableDataCollections = ref<Array<DataCollectionListItem>>([]);
    const dataCollectionsLoading = ref<boolean>(false);

    // Declared before the first await (and before the immediate
    // `connectionOptions` watcher) so the auto-select callback can call it
    // without hitting a TDZ ReferenceError.
    const fetchDataCollections = async (connectionId: string) => {
      if (!connectionId) return;

      try {
        dataCollectionsLoading.value = true;
        // There is currently no JS SDK method to fetch the data collection keys of a connection
        // See https://routine-company.slack.com/archives/C08EGE3TAQ2/p1758031046920299?thread_ts=1758030539.291459&cid=C08EGE3TAQ2
        const response = await membrane.value.get<
          Array<DataCollectionListItem>
        >(`connections/${connectionId}/data`);
        const all = response || [];
        const allowed = getAllowedDataCollections(resolvedApp.value?.key);
        if (allowed !== null && allowed.length > 0) {
          const membraneKeys = new Set(all.map((dc) => dc.key));
          for (const allowedKey of allowed) {
            if (!membraneKeys.has(allowedKey)) {
              window.console.warn(
                `[integrationAllowlist] Data collection key "${allowedKey}" for integration "${resolvedApp.value?.key}" is in the allowlist but was not found in Membrane.`
              );
            }
          }
        }
        availableDataCollections.value =
          allowed !== null ? all.filter((dc) => allowed.includes(dc.key)) : all;
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Failed to fetch data collections:", error);
        availableDataCollections.value = [];
      } finally {
        dataCollectionsLoading.value = false;
      }
    };

    releaser.defer(
      watch(
        connectionOptions,
        (newOptions) => {
          if (newOptions.length === 1 && !selectedConnectionDisplay.value) {
            selectedConnectionDisplay.value = newOptions[0].value;
            selectedConnectionId.value =
              connections.value.find(
                (conn) => conn.name === newOptions[0].value
              )?.id || "";
            // Fetch data collections for the auto-selected account
            if (selectedConnectionId.value) {
              fetchDataCollections(selectedConnectionId.value);
            }
          }
        },
        { immediate: true }
      )
    );

    // Fetch all workspaces for the organization
    const allWorkspaces = await (async () => {
      const workspaceIds = await controller.organization.workspaces.all(
        organizationId.value!
      );

      const workspacePromises = workspaceIds.value.map(async (workspaceId) => {
        const workspace = await controller.workspace.get(workspaceId);
        return {
          id: workspaceId,
          name: workspace.value?.name ?? workspaceId,
        };
      });

      return await Promise.all(workspacePromises);
    })();

    const workspaceOptions: Ref<Array<SelectOption>> = computed(() =>
      allWorkspaces
        .map((workspace) => ({
          value: workspace.id,
          label: workspace.name,
          aliases: [],
        }))
        .sort((a, b) => a.label.localeCompare(b.label))
    );

    releaser.defer(
      watch(
        workspaceOptions,
        (newOptions) => {
          if (newOptions.length === 1 && !selectedWorkspace.value) {
            const workspace = allWorkspaces.find(
              (ws) => ws.id === newOptions[0].value
            );
            if (workspace) {
              selectedWorkspace.value = {
                id: workspace.id,
                name: workspace.name,
              };
            }
          }
        },
        { immediate: true }
      )
    );

    // Make sure the organization's connections are available (retrying a
    // load that previously failed — this is a user-driven settings flow).
    await integrations.ensureLoaded(organizationId.value, {
      reloadOnError: true,
    });

    const dataCollectionSpec = ref<DataCollectionSpec | null>(null);
    const parametersSchema = ref<DataSchema | null>(null);
    const specLoading = ref<boolean>(false);

    // Initialize composables
    const {
      dynamicParameters,
      dynamicFields,
      areDynamicParametersValid,
      initializeDynamicParameters,
      resetDynamicParameters,
      oneOfDiscriminator,
      oneOfBranchFields,
      oneOfError,
      clearBranchParameters,
    } = useMembraneDynamicSchemaForm(parametersSchema);

    const {
      referenceCollectionData,
      referenceCollectionSearchResults,
      referenceCollectionSearchable,
      referenceCollectionSearchQuery,
      referenceCollectionCurrentQuery,
      referenceCollectionLoading,
      referenceCollectionDropdownOpen,
      referenceCollectionSelectedIndex,
      referenceCollectionSelectedLabels,
      referenceCollectionOptions,
      loadReferenceCollectionData,
      loadReferenceCollections,
      handleSearchInput,
      onReferenceCollectionSearch: onReferenceCollectionSearchFromComposable,
      handleReferenceCollectionInput,
      handleReferenceCollectionFocus,
      handleReferenceCollectionBlur,
      selectPreviousOption: selectPreviousOptionFromComposable,
      selectNextOption: selectNextOptionFromComposable,
      validateReferenceCollectionSelection:
        validateReferenceCollectionSelectionFromComposable,
      selectReferenceCollectionOption,
      clearReferenceCollections,
    } = useMembraneReferenceCollections(
      membrane,
      selectedConnectionId,
      dynamicParameters
    );

    /**
     * Extra guidance for the fields of the current integration that need it,
     * pre-split into text / inline-code segments for rendering.
     */
    const fieldNoteSegments = computed<Record<string, Array<NoteSegment>>>(
      () => {
        const notes: Record<string, Array<NoteSegment>> = {};
        for (const field of dynamicFields.value) {
          const note = getIntegrationFieldNote(
            resolvedApp.value?.key,
            field.key
          );
          if (note) notes[field.key] = noteSegments(note);
        }
        return notes;
      }
    );

    const dataCollectionOptions: Ref<Array<SelectOption>> = computed(() =>
      availableDataCollections.value
        .map((dataCollection) => ({
          value: dataCollection.key,
          label: dataCollection.name || dataCollection.key,
          aliases: [dataCollection.key],
        }))
        .sort((a: SelectOption, b: SelectOption) =>
          a.label.localeCompare(b.label)
        )
    );

    const fetchDataCollectionSpec = async (
      connectionId: string,
      dataCollectionKey: string
    ) => {
      if (!connectionId || !dataCollectionKey) return;

      try {
        specLoading.value = true;
        const spec: DataCollectionSpec = await membrane.value
          .connection(connectionId)
          .dataCollection(dataCollectionKey)
          .get();

        dataCollectionSpec.value = spec;
        parametersSchema.value = spec?.parametersSchema ?? null;

        // Reset dynamic parameters when spec changes
        resetDynamicParameters();
        clearReferenceCollections();

        // Initialize dynamic parameters with empty strings to prevent undefined values
        initializeDynamicParameters();

        // Load reference collection data for fields that need it
        const fieldsWithReferences = dynamicFields.value.filter(
          (
            f
          ): f is DynamicField & {
            referenceCollection: NonNullable<
              DynamicField["referenceCollection"]
            >;
          } => f.referenceCollection !== undefined
        );
        await loadReferenceCollections(fieldsWithReferences);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Failed to fetch data collection spec:", error);
        dataCollectionSpec.value = null;
        parametersSchema.value = null;
      } finally {
        specLoading.value = false;
      }
    };

    const onConnectionSelected = async (option: SelectOption) => {
      const connectionId =
        connections.value.find((conn) => conn.name === option.value)?.id || "";
      selectedConnectionId.value = connectionId;
      selectedConnectionDisplay.value = option.value;

      // Reset data collection when connection changes
      selectedDataCollection.value = null;
      parametersSchema.value = null;
      resetDynamicParameters();
      clearReferenceCollections();

      // Fetch data collections for the selected connection
      if (connectionId) {
        await fetchDataCollections(connectionId);
      }
    };

    const onConnectionSelectedValue = async (value: string) => {
      const connectionId =
        connections.value.find((conn) => conn.name === value)?.id || "";
      selectedConnectionId.value = connectionId;
      selectedConnectionDisplay.value = value;

      // Reset data collection when connection changes
      selectedDataCollection.value = null;
      parametersSchema.value = null;
      resetDynamicParameters();
      clearReferenceCollections();

      // Fetch data collections for the selected connection
      if (connectionId) {
        await fetchDataCollections(connectionId);
      }
    };

    const onDataCollectionSelected = async (option: SelectOption) => {
      selectedDataCollection.value = {
        key: option.value,
        name: option.label,
      };

      // Fetch the data collection spec to get parameters schema
      if (selectedConnectionId.value && option.value) {
        await fetchDataCollectionSpec(selectedConnectionId.value, option.value);
      }
    };

    const onDataCollectionSelectedValue = async (value: string) => {
      const option = dataCollectionOptions.value.find(
        (opt) => opt.value === value
      );
      if (option) {
        selectedDataCollection.value = {
          key: option.value,
          name: option.label,
        };

        // Fetch the data collection spec to get parameters schema
        if (selectedConnectionId.value && option.value) {
          await fetchDataCollectionSpec(
            selectedConnectionId.value,
            option.value
          );
        }
      }
    };

    const onWorkspaceSelected = (option: SelectOption) => {
      const workspace = allWorkspaces.find((ws) => ws.id === option.value);
      if (workspace) {
        selectedWorkspace.value = { id: workspace.id, name: workspace.name };
      }
    };

    const onWorkspaceSelectedValue = (value: string) => {
      const workspace = allWorkspaces.find((ws) => ws.id === value);
      if (workspace) {
        selectedWorkspace.value = { id: workspace.id, name: workspace.name };
      }
    };

    // Wrapper functions that integrate composable functions with component logic
    const onReferenceCollectionSearch = async (
      fieldKey: string,
      searchQuery: string
    ) => {
      const field = dynamicFields.value.find((f) => f.key === fieldKey);
      if (!field?.referenceCollection) return;

      await onReferenceCollectionSearchFromComposable(
        fieldKey,
        searchQuery,
        field.referenceCollection
      );
    };

    const handleReferenceCollectionInputWrapper = (
      fieldKey: string,
      value: string
    ) => {
      handleReferenceCollectionInput(fieldKey, value);

      if (referenceCollectionSearchable.value[fieldKey]) {
        handleSearchInput(fieldKey, value, onReferenceCollectionSearch);
      }
    };

    const selectPreviousOption = (fieldKey: string) => {
      selectPreviousOptionFromComposable(fieldKey);
    };

    const selectNextOption = (fieldKey: string) => {
      const maxIndex =
        (referenceCollectionOptions.value[fieldKey]?.length || 1) - 1;
      selectNextOptionFromComposable(fieldKey, maxIndex);
    };

    const validateReferenceCollectionSelection = (fieldKey: string) => {
      validateReferenceCollectionSelectionFromComposable(
        fieldKey,
        selectReferenceCollectionOptionWrapper
      );
    };

    const selectReferenceCollectionOptionWrapper = (
      fieldKey: string,
      option: SelectOption
    ) => {
      selectReferenceCollectionOption(fieldKey, option);
      onParameterChange(fieldKey, option.value);
    };

    const onParameterChange = async (fieldKey: string, value: unknown) => {
      dynamicParameters.value[fieldKey] = value;

      // Check if this is a discriminator field change
      const discriminator = oneOfDiscriminator.value;
      if (discriminator && fieldKey === discriminator.fieldName) {
        // Get current branch fields before clearing
        const previousBranchFields = oneOfBranchFields.value;

        // Clear all branch-specific field values
        clearBranchParameters();

        // Also clear reference collection data for these fields
        for (const field of previousBranchFields) {
          delete referenceCollectionData.value[field.key];
          delete referenceCollectionSearchResults.value[field.key];
          delete referenceCollectionLoading.value[field.key];
          delete referenceCollectionDropdownOpen.value[field.key];
          delete referenceCollectionSelectedIndex.value[field.key];
          delete referenceCollectionSelectedLabels.value[field.key];
          delete referenceCollectionSearchable.value[field.key];
          delete referenceCollectionSearchQuery.value[field.key];
          delete referenceCollectionCurrentQuery.value[field.key];
        }

        // Load reference collections for the new branch
        const newBranchFields = oneOfBranchFields.value;

        // Initialize new branch fields with empty strings
        for (const field of newBranchFields) {
          dynamicParameters.value[field.key] = "";
        }

        const fieldsWithReferences = newBranchFields.filter(
          (
            f
          ): f is DynamicField & {
            referenceCollection: NonNullable<
              DynamicField["referenceCollection"]
            >;
          } => f.referenceCollection !== undefined
        );
        await loadReferenceCollections(fieldsWithReferences);
        return;
      }

      // Check if this parameter change affects any reference collections
      for (const field of dynamicFields.value) {
        if (field.referenceCollection && field.referenceCollection.parameters) {
          const params = field.referenceCollection.parameters;
          // Check if any parameter references this field
          let shouldReload = false;

          for (const [_paramKey, paramValue] of Object.entries(params)) {
            // Unwrap Vue Proxy to get the raw value
            const rawParamValue = toRaw(paramValue);

            // Check for string format: "$workspace_id"
            if (
              typeof rawParamValue === "string" &&
              rawParamValue === `$${fieldKey}`
            ) {
              shouldReload = true;
              break;
            }

            // Check for object format: { "$var": "workspace_id" } or { "$var": "$.workspace_id" }
            if (
              typeof rawParamValue === "object" &&
              rawParamValue !== null &&
              isVarReference(rawParamValue)
            ) {
              const referencedKey = normalizeVarReference(rawParamValue.$var);
              if (referencedKey === fieldKey) {
                shouldReload = true;
                break;
              }
            }
          }

          if (shouldReload) {
            // This reference collection depends on the changed parameter, reload it
            const data = await loadReferenceCollectionData(
              selectedConnectionId.value,
              field.referenceCollection,
              field.key
            );
            referenceCollectionData.value[field.key] = data;

            // Clear the dependent field's value and label since the options changed
            if (dynamicParameters.value[field.key]) {
              dynamicParameters.value[field.key] = "";
            }
            delete referenceCollectionSelectedLabels.value[field.key];
          }
        }
      }
    };

    const isFormValid = computed(() => {
      // Check for oneOf schema errors
      if (oneOfError.value) {
        return false;
      }

      // Check data source name
      if (!databaseDisplayName.value.trim()) {
        return false;
      }

      // Check connection
      if (!selectedConnectionId.value) {
        return false;
      }

      // Check data collection
      if (!selectedDataCollection.value?.key) {
        return false;
      }

      // Check workspace
      if (!selectedWorkspace.value?.id) {
        return false;
      }

      // Check required dynamic parameters using composable
      if (!areDynamicParametersValid.value) {
        return false;
      }

      return true;
    });

    releaser.defer(
      watch(
        isFormValid,
        (valid) => {
          props.onValidityChange?.(valid);
        },
        { immediate: true }
      )
    );

    const onConfirm = async () => {
      if (!isFormValid.value) {
        return;
      }

      const enabledDataCollectionId: IntegrationAppEnabledDataCollectionId = {
        connection_id: selectedConnectionId.value,
        data_collection_key: selectedDataCollection.value!.key,
        parameters: dynamicParameters.value,
      };

      await controller.integrationApp.enableDataCollection(
        selectedWorkspace.value!.id,
        enabledDataCollectionId,
        {
          status: "creation_pending",
          database_display_name: databaseDisplayName.value,
          integration_key: resolvedApp.value?.key,
        }
      );

      if (props.onDone) {
        props.onDone();
      } else {
        navigateTo(RouteNames.SettingsMembraneIntegration, router, {
          params: { orgId: props.orgId, appKey: props.appKey },
        });
      }
    };

    props.onSubmitReady?.(onConfirm);

    /**
     * Helper to check if a field has unsatisfied dependencies
     * Returns the name of the missing dependency field, or null if all satisfied
     */
    const getUnsatisfiedDependency = (
      fieldKey: string
    ): { key: string; label: string } | null => {
      const field = dynamicFields.value.find((f) => f.key === fieldKey);
      if (!field?.referenceCollection?.parameters) return null;

      const params = field.referenceCollection.parameters;
      for (const [_paramKey, paramValue] of Object.entries(params)) {
        const rawParamValue = toRaw(paramValue);

        let referencedParamKey: string | null = null;

        // String format: "$workspace_id"
        if (
          typeof rawParamValue === "string" &&
          rawParamValue.startsWith("$")
        ) {
          referencedParamKey = rawParamValue.slice(1);
        }
        // Object format: { "$var": "workspace_id" } or { "$var": "$.workspace_id" }
        else if (
          typeof rawParamValue === "object" &&
          rawParamValue !== null &&
          isVarReference(rawParamValue)
        ) {
          referencedParamKey = normalizeVarReference(rawParamValue.$var);
        }

        if (referencedParamKey) {
          const referencedValue = dynamicParameters.value[referencedParamKey];
          if (!referencedValue) {
            // Find the label of the referenced field
            const referencedField = dynamicFields.value.find(
              (f) => f.key === referencedParamKey
            );
            return {
              key: referencedParamKey,
              label: referencedField?.label || referencedParamKey,
            };
          }
        }
      }

      return null;
    };

    /**
     * Check if a field should be disabled
     */
    const isFieldDisabled = (fieldKey: string): boolean => {
      // Always disabled if spec is loading
      if (specLoading.value) return true;

      // Check if this field has unsatisfied dependencies
      const unsatisfiedDep = getUnsatisfiedDependency(fieldKey);
      if (unsatisfiedDep) return true;

      // Check if the field is currently loading
      if (referenceCollectionLoading.value[fieldKey]) return true;

      return false;
    };

    /**
     * Reads a dynamic parameter value as a string for binding to string inputs.
     * Parameter values are `unknown` (they can be `{ $var: string }` reference
     * objects), so non-string values render as empty. Writes go through
     * `onParameterChange` via the inputs' `@selected` / `@update:value` events.
     */
    const getParameterStringValue = (fieldKey: string): string => {
      const value = dynamicParameters.value[fieldKey];
      return typeof value === "string" ? value : "";
    };

    /**
     * Get appropriate placeholder for a field
     */
    const getFieldPlaceholder = (field: DynamicField): string => {
      const unsatisfiedDep = getUnsatisfiedDependency(field.key);
      if (unsatisfiedDep) {
        return `First select ${unsatisfiedDep.label}`;
      }

      if (referenceCollectionLoading.value[field.key]) {
        return "Loading options...";
      }

      if (referenceCollectionSearchable.value[field.key]) {
        return `Search ${field.label} (3+ chars)...`;
      }

      return `Select ${field.label}`;
    };

    return {
      resolvedApp,
      selectedConnectionId: selectedConnectionId,
      selectedConnectionDisplay: selectedConnectionDisplay,
      selectedDataCollection,
      connectionOptions: connectionOptions,
      dataCollectionOptions,
      dataCollectionsLoading,
      dynamicFields,
      dynamicParameters,
      fieldNoteSegments,
      referenceCollectionData,
      referenceCollectionSearchResults,
      referenceCollectionSearchable,
      referenceCollectionSearchQuery,
      referenceCollectionCurrentQuery,
      referenceCollectionLoading,
      referenceCollectionDropdownOpen,
      referenceCollectionSelectedIndex,
      referenceCollectionSelectedLabels,
      referenceCollectionOptions,
      specLoading,
      selectedWorkspace,
      databaseDisplayName,
      workspaceOptions,
      isFormValid,
      onConnectionSelected: onConnectionSelected,
      onConnectionSelectedValue,
      onDataCollectionSelected,
      onDataCollectionSelectedValue,
      onWorkspaceSelected,
      onWorkspaceSelectedValue,
      onParameterChange,
      onReferenceCollectionSearch,
      handleReferenceCollectionInput: handleReferenceCollectionInputWrapper,
      handleReferenceCollectionFocus,
      handleReferenceCollectionBlur,
      selectPreviousOption,
      selectNextOption,
      validateReferenceCollectionSelection,
      selectReferenceCollectionOption: selectReferenceCollectionOptionWrapper,
      onConfirm,
      parametersSchema,
      oneOfError,
      isFieldDisabled,
      getFieldPlaceholder,
      getParameterStringValue,
    };
  },
});
</script>

<style scoped lang="scss">
@use "../../styles/settings.scss";

.add-data-collection-modal-body {
  .form-section {
    margin-top: 0;
  }
}

h2 {
  display: flex;
  align-items: center;
  gap: 12px;

  .icon {
    height: 24px;
    width: 24px;
    border-radius: $border-radius-m;
  }
}

.form-section {
  margin-top: 24px;

  .field {
    margin-bottom: 24px;

    label {
      display: block;
      margin-bottom: 8px;
      font-weight: 500;
      font-size: 14px;
      @include themed() {
        color: t($grey-70);
      }

      .required {
        color: #ef4444;
        margin-inline-start: 2px;
      }
    }

    .field-description {
      display: block;
      margin-bottom: 10px;
      margin-top: -4px;
      font-size: 12px;
      white-space: pre-line;
      @include themed() {
        color: t($grey-50);
      }
    }

    .field-note {
      // BaseAlert's `small` size is `width: 100%` with padding, which overflows
      // the field by its padding without a border box.
      box-sizing: border-box;
      align-items: flex-start;
      column-gap: 8px;
      margin-bottom: 10px;

      .field-note-icon {
        width: 16px;
        height: 16px;
        flex-shrink: 0;
        margin-top: 1px;
      }
    }

    .error-message {
      padding: 12px 16px;
      border-radius: $border-radius-m;
      font-size: 14px;
      font-weight: 500;
      background-color: #fee;
      color: #c00;
      border: 1px solid #fcc;
    }
  }

  .dynamic-fields {
    margin-top: 0px;
    padding-top: 0px;
  }

  .dynamic-fields-loading {
    margin-top: 0px;
    padding-top: 0px;
  }

  .input-with-spinner {
    position: relative;

    .spinner-overlay {
      position: absolute;
      inset-inline-end: 8px;
      top: 50%;
      transform: translateY(-50%);
      pointer-events: none;
      z-index: 10;
    }
  }

  .reference-collection-input {
    position: relative;

    .reference-collection-dropdown {
      position: absolute;
      top: calc(100% + 4px);
      inset-inline-start: 0;
      inset-inline-end: 0;
      margin: 0;
      padding: 4px;
      border-radius: $border-radius-l;
      box-shadow: $tooltip;
      z-index: $z-index-dropdown;
      max-height: 200px;
      overflow-y: auto;
      @include themed() {
        background: t($bg);
        border: 1px solid t($grey-10);
      }

      .dropdown-item {
        padding: 6px 8px;
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        border-radius: $border-radius-m;
        @include themed() {
          color: t($grey-70);
        }

        &.selected {
          @include themed() {
            color: t($primary);
            background: t($grey-10);
          }
        }

        &:hover {
          @include themed() {
            color: t($primary);
            background: t($grey-10);
          }
        }

        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
    }

    .spinner-overlay {
      position: absolute;
      inset-inline-end: 8px;
      top: 50%;
      transform: translateY(-50%);
      pointer-events: none;
      z-index: 10;
    }
  }

  .loading-container {
    display: flex;
    align-items: center;
    padding: 8px 0;
  }

  .final-fields {
    margin-top: 24px;

    .form-actions {
      margin-top: 32px;
      display: flex;
      justify-content: flex-end;
    }
  }

  code {
    background-color: rgba(0, 0, 0, 0.05);
    padding: 2px 4px;
    border-radius: 3px;
    font-family: monospace;
    font-size: 0.9em;
    @include themed() {
      background-color: t($grey-05);
    }
  }
}
</style>
